export type AppostleDirectiveWorkflow = "compose_then_tool" | "tool_first";

export type AppostleDirectiveDefinition = {
  id: string;
  trigger: `>${string}`;
  label: string;
  description: string;
  workflow: AppostleDirectiveWorkflow;
  toolName: string;
  routedPrompt?: string;
};

function toolFirstPrompt(params: {
  directive: string;
  toolName: string;
  instruction: string;
  after?: string;
}): string {
  return [
    `This user message is the Appostle directive \`${params.directive}\`.`,
    "",
    `Call \`mcp__appostle__${params.toolName}\` as your first action for this directive.`,
    params.instruction,
    "",
    params.after ??
      "After the tool call succeeds, answer the user briefly with the result. Do not do unrelated work in this turn.",
  ].join("\n");
}

export const APPOSTLE_DIRECTIVES = [
  {
    id: "learn",
    trigger: ">learn",
    label: ">learn",
    description: "Summarise in plain English",
    workflow: "tool_first",
    toolName: "learn",
    routedPrompt: toolFirstPrompt({
      directive: ">learn",
      toolName: "learn",
      instruction:
        "Use the directive text and recent conversation context as the explanation target.",
      after:
        "After the tool call succeeds, follow its returned instruction and explain the latest relevant outcome in simpler plain language.",
    }),
  },
  {
    id: "handoff",
    trigger: ">handoff",
    label: ">handoff",
    description: "Hand off context to a new agent session",
    workflow: "compose_then_tool",
    toolName: "handoff",
    routedPrompt: [
      "This user message is the Appostle directive `>handoff`.",
      "",
      "Use the current conversation and workspace context to write a good task brief for a child agent, then call `mcp__appostle__handoff`.",
      "",
      "The child brief must be self-contained: goal, relevant files/paths, constraints, decisions already made, and what the child should report back.",
      "",
      "After the tool call succeeds, tell the user in one short sentence what brief you sent. Do not mention child/session/agent IDs unless the user explicitly asks. Do not do the delegated work yourself.",
    ].join("\n"),
  },
  {
    id: "report",
    trigger: ">report",
    label: ">report",
    description: "Send a curated message back to the parent session",
    workflow: "tool_first",
    toolName: "report",
    routedPrompt: toolFirstPrompt({
      directive: ">report",
      toolName: "report",
      instruction:
        "Use the user's directive text as the report content unless the user clearly asks you to summarize the current session first. The report must be self-contained because the parent cannot see this timeline.",
      after:
        "After the tool call succeeds, tell the user in one short sentence that the report was sent.",
    }),
  },
  {
    id: "talkback",
    trigger: ">talkback",
    label: ">talkback",
    description: "Send a follow-up into a finished disciple's session",
    workflow: "tool_first",
    toolName: "talkback",
    routedPrompt: toolFirstPrompt({
      directive: ">talkback",
      toolName: "talkback",
      instruction:
        "Parse the target disciple/session id and follow-up message from the directive text. If the target id is missing or ambiguous, ask for it instead of guessing.",
      after:
        "After the tool call succeeds, tell the user in one short sentence that the follow-up was sent.",
    }),
  },
  {
    id: "chain",
    trigger: ">chain",
    label: ">chain",
    description: "Engage interrogate-then-delegate mode in this session",
    workflow: "tool_first",
    toolName: "chain",
    routedPrompt: toolFirstPrompt({
      directive: ">chain",
      toolName: "chain",
      instruction:
        "Use the directive text as the seed brief for chain mode. Do not start implementation before the chain contract is established.",
      after: "After the tool call succeeds, follow the chain contract returned by the tool.",
    }),
  },
  {
    id: "todo",
    trigger: ">todo",
    label: ">todo",
    description: "Add a short reminder to the project's Todo dropdown",
    workflow: "tool_first",
    toolName: "todo",
    routedPrompt: toolFirstPrompt({
      directive: ">todo",
      toolName: "todo",
      instruction:
        "Use the directive text to synthesize the todo title, notes, subtasks, and merge target if appropriate. Do not ask for confirmation unless the structure is genuinely ambiguous.",
      after:
        "After the tool call succeeds, tell the user in one short sentence that the todo was added or updated.",
    }),
  },
  {
    id: "write_plan",
    trigger: ">write_plan",
    label: ">write_plan",
    description: "Write a structured plan into the Plans modal",
    workflow: "tool_first",
    toolName: "write_plan",
    routedPrompt: toolFirstPrompt({
      directive: ">write_plan",
      toolName: "write_plan",
      instruction:
        "Use the directive text and current context to create a concise structured plan. Keep todos concrete and avoid filler.",
      after:
        "After the tool call succeeds, tell the user in one short sentence which plan was written.",
    }),
  },
  {
    id: "complete_todo",
    trigger: ">complete_todo",
    label: ">complete_todo",
    description: "Mark the spawning todo as completed",
    workflow: "tool_first",
    toolName: "complete_todo",
    routedPrompt: toolFirstPrompt({
      directive: ">complete_todo",
      toolName: "complete_todo",
      instruction:
        "Use the todo id from the current session's todo-spawn context or from the directive text. Only call the tool when the user has confirmed this todo is done.",
      after: "After the tool call succeeds, acknowledge completion in one short sentence.",
    }),
  },
  {
    id: "cross_hosts_terminal",
    trigger: ">cross_hosts_terminal",
    label: ">cross_hosts_terminal",
    description: "Run a command on a reachable sibling host",
    workflow: "tool_first",
    toolName: "cross_hosts_terminal",
    routedPrompt: toolFirstPrompt({
      directive: ">cross_hosts_terminal",
      toolName: "cross_hosts_terminal",
      instruction:
        "Parse the target host, command, and optional cwd from the directive text. If the host or command is missing, ask for the missing field instead of guessing.",
      after:
        "After the tool call succeeds, summarize the exit status and key output. Preserve important stdout/stderr details.",
    }),
  },
  {
    id: "cross_hosts_transfer",
    trigger: ">cross_hosts_transfer",
    label: ">cross_hosts_transfer",
    description: "Transfer a file to or from a reachable sibling host",
    workflow: "tool_first",
    toolName: "cross_hosts_transfer",
    routedPrompt: toolFirstPrompt({
      directive: ">cross_hosts_transfer",
      toolName: "cross_hosts_transfer",
      instruction:
        "Parse the target host, direction, remote path, and local path from the directive text. If any required field is missing, ask for it instead of guessing.",
      after:
        "After the tool call succeeds, summarize the transfer direction and destination in one short sentence.",
    }),
  },
] as const satisfies readonly AppostleDirectiveDefinition[];

export type AppostleDirectiveId = (typeof APPOSTLE_DIRECTIVES)[number]["id"];

export function findAppostleDirective(input: string): AppostleDirectiveDefinition | null {
  const match = input.match(/^\s*>([A-Za-z0-9_]+)(?:\s|$)/);
  if (!match) return null;
  const id = match[1]?.toLowerCase();
  return APPOSTLE_DIRECTIVES.find((directive) => directive.id === id) ?? null;
}

export function buildRoutedDirectivePrompt(input: string): string | null {
  const directive = findAppostleDirective(input);
  if (!directive || !directive.routedPrompt) {
    return null;
  }
  return [directive.routedPrompt, "", "Original user directive:", input.trim()].join("\n");
}
